import { defineComponent as _defineComponent } from 'vue';
import { createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "simplePage"
};
const _hoisted_2 = { class: "dial-wrap" };
const _hoisted_3 = ["onClick"];
const _hoisted_4 = { class: "dial-center" };
const _hoisted_5 = { class: "dial-center-value" };
const _hoisted_6 = { class: "dial-center-action" };
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAppState } from '@/composables/useAppState';
import { useAuth } from '@/composables/useAuth';
import { useCustomer } from '@/composables/useCustomer';
import { useBanking } from '@/composables/useBanking';
import { useSpending } from '@/composables/useSpending';
import { clearSpendingAnalysisCache } from '@/composables/useSpendingAnalysis';
import CustomerLoadWait from './elements/CustomerLoadWait.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import MGButton from './elements/MGButton.vue';
import { vibrate } from '@/utils/native';
const STEP = 360 / 31;
const RADIUS_PCT = 87;
export default /*@__PURE__*/ _defineComponent({
    __name: 'SetupSpendingAnalysisPage',
    setup(__props) {
        const router = useRouter();
        const state = useAppState();
        useAuth();
        const { getCustomer } = useCustomer();
        const { getBankingInfo, clearBankingInfo } = useBanking();
        const { setupSpendingCycle } = useSpending();
        const cycleId = ref(null);
        const isSubmitting = ref(false);
        const rotation = ref(0);
        const isDragging = ref(false);
        const isInertia = ref(false);
        const ringEl = ref(null);
        const selectedDay = computed(() => {
            const idx = ((Math.round(-rotation.value / STEP) % 31) + 31) % 31;
            return idx + 1;
        });
        // Tick the native wrapper's Vibration channel as the dial moves between days.
        watch(selectedDay, () => vibrate('selection'));
        function dayStyle(day) {
            const angle = ((day - 1) / 31) * 2 * Math.PI - Math.PI / 2;
            const r = RADIUS_PCT / 2;
            const x = 50 + r * Math.cos(angle);
            const y = 50 + r * Math.sin(angle);
            return { left: `${x}%`, top: `${y}%` };
        }
        function rotateToDay(day) {
            const target = -((day - 1) * STEP);
            const current = rotation.value;
            const delta = ((target - current + 540) % 360) - 180;
            rotation.value = current + delta;
        }
        function selectDay(day) {
            rotateToDay(day);
        }
        let startAngle = 0;
        let startRotation = 0;
        let totalDelta = 0;
        let suppressClick = false;
        let lastMoveAngle = 0;
        let lastMoveTs = 0;
        let velocity = 0;
        let inertiaRaf = 0;
        function pointerAngle(e) {
            const rect = ringEl.value.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            return Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI;
        }
        function cancelInertia() {
            if (inertiaRaf) {
                cancelAnimationFrame(inertiaRaf);
                inertiaRaf = 0;
            }
            isInertia.value = false;
        }
        function onPointerDown(e) {
            if (!ringEl.value)
                return;
            cancelInertia();
            startAngle = pointerAngle(e);
            startRotation = rotation.value;
            totalDelta = 0;
            lastMoveAngle = startAngle;
            lastMoveTs = performance.now();
            velocity = 0;
            isDragging.value = true;
            suppressClick = false;
            window.addEventListener('pointermove', onPointerMove);
            window.addEventListener('pointerup', onPointerUp);
            window.addEventListener('pointercancel', onPointerUp);
        }
        function onPointerMove(e) {
            if (!isDragging.value)
                return;
            const a = pointerAngle(e);
            let delta = a - startAngle;
            if (delta > 180)
                delta -= 360;
            if (delta < -180)
                delta += 360;
            totalDelta = delta;
            rotation.value = startRotation + delta;
            const now = performance.now();
            let inc = a - lastMoveAngle;
            if (inc > 180)
                inc -= 360;
            if (inc < -180)
                inc += 360;
            const dt = now - lastMoveTs;
            if (dt > 0) {
                const instant = inc / dt;
                velocity = velocity * 0.5 + instant * 0.5;
            }
            lastMoveAngle = a;
            lastMoveTs = now;
        }
        function onPointerUp() {
            if (!isDragging.value)
                return;
            isDragging.value = false;
            if (Math.abs(totalDelta) > 3) {
                suppressClick = true;
            }
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('pointercancel', onPointerUp);
            if (Math.abs(velocity) > 0.05) {
                startInertia();
            }
            else if (Math.abs(totalDelta) > 3) {
                rotation.value = Math.round(rotation.value / STEP) * STEP;
            }
        }
        function startInertia() {
            isInertia.value = true;
            let lastTs = performance.now();
            const tick = (ts) => {
                const dt = Math.min(ts - lastTs, 32);
                lastTs = ts;
                rotation.value += velocity * dt;
                velocity *= Math.pow(0.94, dt / 16);
                if (Math.abs(velocity) < 0.01) {
                    rotation.value = Math.round(rotation.value / STEP) * STEP;
                    inertiaRaf = 0;
                    isInertia.value = false;
                    return;
                }
                inertiaRaf = requestAnimationFrame(tick);
            };
            inertiaRaf = requestAnimationFrame(tick);
        }
        function onDayClick(day, e) {
            if (suppressClick) {
                e.preventDefault();
                e.stopPropagation();
                suppressClick = false;
                return;
            }
            rotateToDay(day);
        }
        function onLoad() {
            getCustomer(() => {
                getBankingInfo(() => {
                    cycleId.value = state.bankInfo.spendingCycle.id;
                    const day = state.bankInfo.spendingCycle.day || 1;
                    rotation.value = -((day - 1) * STEP);
                });
            });
        }
        function setPaymentCycle() {
            if (isSubmitting.value)
                return;
            isSubmitting.value = true;
            setupSpendingCycle(state.customer.id, cycleId.value, selectedDay.value, true, () => {
                clearBankingInfo();
                clearSpendingAnalysisCache();
                router.push({ path: '/activity' });
            });
        }
        onMounted(() => {
            onLoad();
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(HeaderBlock, { title: "Spending Analysis" }),
                _createVNode(CustomerLoadWait, { customer: cycleId.value }, null, 8, ["customer"]),
                _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                        (cycleId.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                                _cache[2] || (_cache[2] = _createElementVNode("h1", null, "Set Pay Day", -1)),
                                _cache[3] || (_cache[3] = _createElementVNode("p", { class: "subtitle" }, "Pick the day your pay lands so we can align your monthly view.", -1)),
                                _createElementVNode("div", _hoisted_2, [
                                    _createElementVNode("div", {
                                        ref_key: "ringEl",
                                        ref: ringEl,
                                        class: _normalizeClass(["dial", { dragging: isDragging.value || isInertia.value }]),
                                        onPointerdown: onPointerDown
                                    }, [
                                        _createElementVNode("div", {
                                            class: "dial-ring",
                                            style: _normalizeStyle({ transform: `rotate(${rotation.value}deg)` })
                                        }, [
                                            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(31, (day) => {
                                                return _createElementVNode("button", {
                                                    key: day,
                                                    class: _normalizeClass(["dial-day", { selected: selectedDay.value === day }]),
                                                    style: _normalizeStyle(dayStyle(day)),
                                                    onClick: ($event) => (onDayClick(day, $event))
                                                }, [
                                                    _createElementVNode("span", {
                                                        class: "dial-day-label",
                                                        style: _normalizeStyle({ transform: `rotate(${-rotation.value}deg)` })
                                                    }, _toDisplayString(day), 5)
                                                ], 14, _hoisted_3);
                                            }), 64))
                                        ], 4),
                                        _cache[1] || (_cache[1] = _createElementVNode("div", { class: "dial-pin" }, null, -1)),
                                        _createElementVNode("div", _hoisted_4, [
                                            _cache[0] || (_cache[0] = _createElementVNode("span", { class: "dial-center-label" }, "Day", -1)),
                                            _createElementVNode("span", _hoisted_5, _toDisplayString(selectedDay.value ?? '—'), 1),
                                            _createElementVNode("div", _hoisted_6, [
                                                _createVNode(MGButton, {
                                                    text: "Set",
                                                    noCaps: true,
                                                    alt: false,
                                                    disabled: isSubmitting.value,
                                                    onClick: setPaymentCycle
                                                }, null, 8, ["disabled"])
                                            ])
                                        ])
                                    ], 34)
                                ])
                            ]))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                })
            ], 64));
        };
    }
});
